/*
 * Copyright (c) 2005-2024, Paycheck Planner
 * All rights reserved
 * 
 */


import Parser from "./runtime-parser/parser.js";

import { model_runtime } from './runtime.js';



const ITEM_START = "_start";
const ITEM_STOP = "_stop";


export class SimulationModel {
  constructor(name) {
    this.parser = new Parser();
    this.parseErrors = [];


    this.baseDesc = name;

    this.startDate = new Date();

    this.categoryTypes = [];

    this.periods = [];
    this.summary = [];

    this.parameters = [];
    this.milestones = [];

    this.accts = [];
    this.items = [];
    this.loans = [];
    this.assets = [];

    this.scenarios = [];

    this.elements = [];
    this.elements.push({}); // Cover case of net worth for now

    this.symbols = this.createSymbolTree();

    console.log('sim model');
  }

  //=========================================================================================
  
  createSymbolTree() {
    let tree = {};
  
    tree.params = [];
    tree.allNames = [];
  
    return tree;
  }


  /**
   * Return the javascript date value for a given milestone
   * @param {string} name
   * @returns {Date}
   */
  getMilestoneDate( name ) {
    return this.symbols[name].value;
  }


  /**
   * Return number of depedents for milestone
   * @param {string} name
   * @returns {number}
   */
  getMilestoneDependencyCount(name) {
    let m = this.symbols[name];

    return m.uiDependents.length;
  }


  /**
   * Return the javascript date value for a given Items start date
   * @param {string} name
   * @returns {Date}
   */ 
  getItemStartDate( name ) {
    return this.symbols[name + ITEM_START].value;
  }


  /**
   * Return the javascript date value for a given Items stop date
   * @param {string} name
   * @returns {Date}
   */
  getItemStopDate( name ) {
    return this.symbols[name + ITEM_STOP].value;
  }


  //------------------------------------------------------------------------------------
  //------------------------------------------------------------------------------------

  findParameterById(id) {
    let param = this.parameters.find(p => p.baseID === id);

    return param === undefined ? null : param;
  }


  findParameterByName(name) {
    let lname = name.toLowerCase();
    let param = this.parameters.find(p => p.name === lname);

    return param === undefined ? null : param;
  }


  /**
   * Given a user model account id, find the corresponding simulation account
   * @param {number} id
   */
  findAccountById(id) {
    const num_accounts = this.accts.length;

    for (let i = 0; i < num_accounts; i++) {
      if (this.accts[i].baseID === id)
        return this.accts[i];
    }

    return null;
  }


  findItemById(id) {
    let item = this.items.find(i => i.baseID === id);

    return item === undefined ? null : item;
  }


  findItemByName(name) {
    let lname = name.toLowerCase();
    let item = this.items.find(i => i.baseDesc.toLowerCase() === lname);

    return item === undefined ? null : item;
  }


  createCategoryType(index, group) {
    let obj = {
      index,
      type: group.type,
      code: group.code,
      sign: group.groupType,
      desc: group.desc
    };

    this.categoryTypes.push(obj);

    return obj;
  }


  createReportingPeriod(from, to, desc, inPast) {

    var period = {
      inPast,
      
      fromMonth: from,
      toMonth: to,

      periodName: desc, // NOT USED
      displayLabel: '',

      totalCash: 0,
      totalDebt: 0,

      score: '',

      months: []
    }

    this.periods.push(period);

    return period;
  }


  createParameter(id, name, type) {
    let obj = {
      baseID: id,

      name: name.toLowerCase(),
      type: type,

      value: null,
      adjustedValue: null
    };

    this.parameters.push(obj);

    return obj;
  }


  createMilestone(id, name, desc, source) {
    let obj = {
      baseID: id,

      name: name, //.toLowerCase(),
      desc: desc.length > 0 ? desc : name,
      source: source,
    };

    this.milestones.push(obj);

    return obj;
  }


  /**
   * 
   * @param {number} id 
   * @param {string} desc 
   * @returns SimulationAccount
   */
  createAccount(id, desc) {
    var acct = {
      baseIndex: this.elements.length,
      index: this.accts.length,

      baseID: id,
      baseDesc: desc,

      lastUpdate: new Date(),
      lastUpdateNum: 0, // yyyymmdd
      lastUpdateDays: 0,

      acctType: 0,
      acctGroup: 0,
      isAsset: false,
      
      serviceAcctId: -1,
      serviceDay: 0,
      serviceSimAcct: null,

      isTaxable: false,
      
      rate: 0,
      limit: 0,
      threshold: 0,
      taxrate: 0,

      skippedAmt: 0,
      startingBal: 0,
      runningBal: 0,

      totalInterest: 0,

      finalBal: 0,

      score: 0
    }

    this.elements.push(acct);
    this.accts.push(acct);

    return acct;
  }


  createItem(id, desc) {
    var item = {
      baseIndex: this.elements.length, // Index in the elements array
      index: this.items.length,

      hasQuickControl: false,

      active: false,
      adjustedActive: false,

      baseID: id,
      baseDesc: desc,
      baseName: '', // TODO this may move shortly JC 16-Jun-2024

      itemType: 0,
      subType: 0,

      freqNum: 0,
      amount: "",

      srcActRef: null, // Used in items list to group by account
      
      proposedAmount: 0,
      adjustedAmount: 0,

      startSource: "",
      endSource: "",
      startExprName: "",
      endExprName: "", 

      startDate: new Date(),
      nextDate: new Date(),
      endDate: new Date(),

      fromCatIdx: 0,
      toCatIdx: 0,

      fromSimAcct: null,
      toSimAcct: null,

      ruleIDs: [],

      modifiers: [],
    }

    this.elements.push(item);
    this.items.push(item);

    return item;
  }


  createLoan(id, desc) {
    var loan = {
      baseIndex: this.elements.length,
      index: this.loans.length,

      baseID: id,
      baseDesc: desc,

      name: 'Loan',

      fromSimAcct: null,

      period: 0,
      rate: 0,

      originalAmount: 0,

      mortgagePayment: 0,
      insurance: 0,
      tax: 0,
      totalPayment: 0,

      currentValue: 0,
    }

    this.elements.push(loan);
    this.loans.push(loan);

    return loan;
  }


  createScenario() {
    var element = {
      adjustmentAmount: 0,

      startNum: 0,
      endNum: 99999999
    };

    this.scenarios.push(element);

    return element;
  }


  //-------------------------------------------------------------------------------------------
  // Parsing section
  //-------------------------------------------------------------------------------------------

  getExpressionValue(name) {
    return this.symbols[name].value
  }


  /**
   * 
   * @param {string} expr 
   * @returns ParseResult
   */
  parseExpression(expr) {
    let ret = {
      errors: false,
      message: "",

      source: expr,
      ast: null,
    }

    let res = this.parser.parseModelExpression( expr );

    if (res.errors === false) {
      ret.ast = res.expr;
    } else {
      ret.errors = true;
      ret.message = res.message;
    }
 
    return ret;
  }


  parseMilestoneDefinition(milestone) {
    this.parseExpressionDefinition(milestone, milestone.name, milestone.source);
  }


  parseItemDefinition(item) {
    item.startExprName = this.parseExpressionDefinition(item, item.baseName + ITEM_START, item.startSource);
    item.endExprName = this.parseExpressionDefinition(item, item.baseName + ITEM_STOP, item.endSource);
  }


  parseExpressionDefinition(owner, name, source) {
    let res = this.parser.parseModelExpression(source); // { errors, expr, message }

    // Maybe this should be hard crash, editor should catch this
    if (res.errors) {
      let err = {
        name: name,
        message: result.message
      };

      this.parseErrors.push(err);

      return -1;
    }


    var sym = {
      owner: owner,  // Owner of the expression like milestone or item, ...
      name: name,
      expr: res.expr,
      value: null,
      dependents: [],      // Outgoing edges - things that depend on me
      dependencies: [],    // Incoming edges - things I depend on
      uiDependents: []     // Same as dependents, used for UI
    };

    this.symbols[name] = sym;
    this.symbols.allNames.push(name);

    return name;
  }

  //-------------------------------------------------------------------------------------------
  // Execution section
  //-------------------------------------------------------------------------------------------

  evaluateExpression(ast) {
    let val = model_runtime.evaluateExpression( this.symbols, ast );
  
    return val;
  }


  evaluateAllExpressions() {
    const date_res = model_runtime.evaluateModelDates(this);
  }

  
}




